/** @jsx jsx */
import { Button, Columns, jsx, Tiles } from '@front10/base-elements';
import { useTranslation } from '@front10/utils';
import { useEffect, useState } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { Combobox } from '../../lab/Combobox';
import { DateRangePicker, Dates } from '../../lab/DateRangePicker';
import { Disclaimer } from '../../lab/Disclaimer';
import { ErrorMessage } from '../../lab/ErrorMessage';
import { FetchAirlines, useAirlines } from './airlines';
import { Airport, AirportMarketFilter, FetchAirports, useAirports } from './airports';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { OriginDestinationLogic } from '../_commons/filters';
import React from 'react';
import { BlackoutDates } from './blackoutDates';

export type BookingFilterLabels = Partial<
  Record<'submit' | 'origin' | 'destination' | 'return' | 'departure', 'string'>
>;

const ENTER_KEY = 13;

interface Props {
  defaultOrigin: Airport;
  prepopulatedAirlines?: string;
  selectedDepartureDate: Date | undefined;
  selectedReturnDate: Date | undefined;
  defaultDestination: Airport;
  defaultDates?: Dates;
  fetchAirports: FetchAirports;
  fetchAirlines: FetchAirlines;
  filterByMarket?: AirportMarketFilter;
  showOrigin?: boolean;
  showDestination?: boolean;
  showTimePickers?: boolean;
  onOriginChange: (origin: Airport) => void;
  onAirlineChange: (airline: { value: string; label: string }) => void;
  onDestinationChange: (destination: Airport) => void;
  onDepartureDateChange: (departureDate: Date | undefined, returnDate: Date | undefined) => void;
  onReturnDateChange: (returnDate: Date | undefined, departureDate: Date | undefined) => void;
  labels: BookingFilterLabels;
  componentsConfigurations: Pick<
    BookingComponentsConfigurations,
    'locationSelector' | 'dateSelector' | 'flexibleDatesSelector' | 'airlinesSelector'
  >;
  blackoutDates?: {
    minDate: number;
    maxDate: number;
  };
  maxTripDuration?: number;
  updateSelectedOrigin: (selectedOrigin: string) => void;
  updateSelectedDestination: (selectedDestination: string) => void;
  updateSelectedDepartureDate: (departureDate: Date | undefined) => void;
  updateSelectedReturnDate: (departureDate: Date | undefined) => void;
  columns: number[][];
  rows?: string[];
  ctaHalfWidth?: boolean;
  showAirlines?: boolean;
  airlines?: {
    airlinesList: Array<{}>;
    enabled: boolean;
  };
  singleDateSelection: boolean;
  activeTab?: number;
  currentTab?: number;
  originDestinationLogic: string;
  trigger?: any;
  onSubmit?: any;
  blackoutDatesIntegration?: boolean;
  flightType?: string;
  fetchBlackoutDates?: BlackoutDates | null;
  setValue?: (key: string, value: any) => void;
  getValues?: () => any;
  disclaimer?: any;
}

export type AirlinesList = Array<{
  labelKey: string;
  defaultValue: string;
  airlineCode: string;
}>;
export type AirlinesConfiguration = {
  airlinesList: AirlinesList;
  enabled: boolean;
};

const timeOptions = ['10:30 AM', '2:00 PM', '5:00 PM'].map((v) => ({
  value: v,
  label: v
}));

export type Airlines = {
  label: string;
  value: string;
};

export type DatePickerLabel = {
  departure: string;
  return: string;
};

export function BookingFilters(
  props: Props &
    Pick<
      UseFormMethods<{
        origin?: Airport;
        destination?: Airport;
        dates?: Dates;
        airlines?: Airlines;
      }>,
      'watch' | 'errors' | 'control'
    >
) {
  const translate = useTranslation();
  const errors = props.errors;
  const labels = props.labels;
  const selectedOrigin = props.watch('origin', props.defaultOrigin);
  const isOriginPrepopulated = props.defaultOrigin && props.defaultOrigin.value !== '';
  const [wasSetedPrepopulatedOriginValue, setWasSetedPrepopulatedOriginValue] = React.useState(
    false
  );
  const isDestinationPrepopulated =
    props.defaultDestination && props.defaultDestination.value !== '';
  const [
    wasSetedPrepopulatedDestinationValue,
    setWasSetedPrepopulatedDestinationValue
  ] = React.useState(false);
  const selectedAirlines = props.watch('airlines', {
    value: props.prepopulatedAirlines ?? '',
    label: props.prepopulatedAirlines ?? ''
  });
  const selectedDestination = props.watch('destination', props.defaultDestination);
  const [selectedDates, setSelectedDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null
  });

  const locationSelector = props.componentsConfigurations.locationSelector ?? {};
  const originTitle = props.componentsConfigurations.locationSelector?.originTitle
    ? translate(props.componentsConfigurations.locationSelector?.originTitle)
    : '';
  const destinationTitle = props.componentsConfigurations.locationSelector?.destinationTitle
    ? translate(props.componentsConfigurations.locationSelector?.destinationTitle)
    : '';
  const datePicker = props.componentsConfigurations.dateSelector;
  const flexibleDates = props.componentsConfigurations.flexibleDatesSelector;
  const airline = props.componentsConfigurations.airlinesSelector;

  const filterByMarket = props.filterByMarket ?? AirportMarketFilter.NONE;

  const showOrigin = props.showOrigin ?? true;
  const showDestination = props.showDestination ?? true;

  const avoidCall = props.originDestinationLogic === OriginDestinationLogic.SAME;

  const { data: origins = [] } = useAirports(props.fetchAirports, {
    airportType: 'ORIGIN',
    filterByMarket
  });

  let { data: destinations = [] } = useAirports(props.fetchAirports, {
    airportType: 'DESTINATION',
    selectedOrigin: selectedOrigin?.value ? selectedOrigin.value : undefined,
    filterByMarket
  });

  if (avoidCall && destinations.length === 0) {
    destinations = [...origins];
  }

  const { data: fetchAirlines = [] } = useAirlines(props.fetchAirlines, {
    selectedOrigin: props.airlines?.enabled ? selectedOrigin?.value : '',
    selectedDestination: props.airlines?.enabled ? selectedDestination?.value : '',
    enableFilter: props.showAirlines
  });

  function findIataCode(iataCode: string, airlines: any) {
    if (airlines) {
      for (let index = 0; index < airlines.length; index++) {
        const airline = airlines[index];
        if (airline.airlineCode === iataCode) {
          return airline;
        }
      }
    }
    return null;
  }

  function getAvailableAirlines(origin: string, destination: string, airlines: AirlinesList) {
    let newOptions = [];
    for (let index = 0; index < fetchAirlines.length; index++) {
      const element = fetchAirlines[index];
      if (element.valid) {
        const findElement = findIataCode(element.iataCode, airlines);
        if (findElement !== null) {
          newOptions.push({
            value: findElement.airlineCode,
            label: translate(findElement.labelKey, findElement.defaultValue)
          });
        }
      }
    }
    return newOptions;
  }

  function disabledArLineFilter() {
    return (
      !selectedOrigin ||
      !selectedDestination ||
      selectedOrigin?.value === '' ||
      selectedDestination?.value === ''
    );
  }

  function getOptions(airlines: any) {
    const origin = selectedOrigin?.value;
    const destination = selectedDestination?.value;
    const airlinesList = airlines.airlinesList;
    let newOptions: any[] = [];
    if (airlinesList !== undefined && airlinesList.length > 0) {
      if (origin === '' && destination === '') {
        for (let index = 0; index < airlinesList.length; index++) {
          const airline = airlinesList[index];
          newOptions.push({
            value: airline.airlineCode,
            label: translate(airline.labelKey, airline.defaultValue)
          });
        }
      } else if (origin === '' || destination === '') {
        return [];
      } else if (origin && origin !== '' && destination && destination !== '') {
        newOptions = getAvailableAirlines(origin, destination, airlinesList);
      }
    }
    return newOptions;
  }

  const airlinesOptions = props.airlines !== undefined ? getOptions(props.airlines) : [];

  useEffect(() => {
    props.updateSelectedOrigin(selectedOrigin?.value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrigin]);

  useEffect(() => {
    props.updateSelectedDestination(selectedDestination?.value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDestination]);

  const DateRangePickerRef = React.useRef<any>(null);
  return (
    <Columns variant="layout.filters" gap={2} columns={props.columns} rows={props.rows}>
      {showOrigin ? (
        <Controller
          render={({ onChange }) => (
            <Combobox
              descriptionIconCloseAction={translate(
                'cmp-clean-button-action-description-origin',
                'Location cleared'
              )}
              isPrepopulatedValue={isOriginPrepopulated}
              wasSetedPrepopulatedValue={wasSetedPrepopulatedOriginValue}
              setWasSetedPrepopulatedValue={setWasSetedPrepopulatedOriginValue}
              defaultValue={props.defaultOrigin.value}
              label={translate(labels.origin || 'From')}
              options={origins}
              active={Boolean(selectedOrigin?.value)}
              onChange={(newValue) => {
                if (newValue.value && newValue.value !== selectedOrigin?.value) {
                  props.onOriginChange(newValue);
                }
                onChange(newValue);
              }}
              errorMessage={<ErrorMessage errors={errors} name="origin" />}
              hasError={!!errors['origin']}
              placeholder={translate(
                'cmp-booking-flight-origin-placeholder',
                'Choose city or airport'
              )}
              onKeyDown={(e) => {
                if (e.keyCode === ENTER_KEY) {
                  setTimeout(() => {
                    if (props?.trigger) {
                      props?.trigger('origin');
                    }
                  }, 200);
                }
              }}
              ariaLabelIconClose={translate('cmp-clear-icon', 'Clear')}
              ariaLabelIconToggle={translate('cmp-clear-icon-toggle', 'Clear')}
              {...locationSelector}
              title={originTitle}
            />
          )}
          name="origin"
          control={props.control}
          rules={{
            validate: {
              required: (origin) =>
                !!origin.label || translate('cmp-origin-validation-message', 'Origin is required')
            }
          }}
        />
      ) : null}
      {showDestination ? (
        <Controller
          render={({ onChange }) => (
            <Combobox
              descriptionIconCloseAction={translate(
                'cmp-clean-button-action-description-destination',
                'Location cleared'
              )}
              isPrepopulatedValue={isDestinationPrepopulated}
              wasSetedPrepopulatedValue={wasSetedPrepopulatedDestinationValue}
              setWasSetedPrepopulatedValue={setWasSetedPrepopulatedDestinationValue}
              defaultValue={props.defaultDestination.value}
              hasError={!!errors['destination']}
              errorMessage={<ErrorMessage errors={errors} name="destination" />}
              placeholder={translate(
                'cmp-booking-flight-destination-placeholder',
                'Choose city or airport'
              )}
              label={translate(labels.destination || 'To')}
              active={Boolean(selectedDestination?.value)}
              onChange={(newValue) => {
                if (newValue.value && newValue.value !== selectedDestination?.value) {
                  props.onDestinationChange(newValue);
                }
                onChange(newValue);
              }}
              options={destinations}
              ariaLabelIconClose={translate('cmp-clear-icon', 'Clear')}
              ariaLabelIconToggle={translate('cmp-clear-icon-toggle', 'Clear')}
              onKeyDown={(e) => {
                if (e.keyCode === ENTER_KEY) {
                  setTimeout(() => {
                    if (props?.trigger) {
                      props?.trigger('destination');
                    }
                  }, 200);
                }
              }}
              {...locationSelector}
              title={destinationTitle}
            />
          )}
          name="destination"
          control={props.control}
          rules={{
            validate: {
              required: (destination) =>
                !!destination.label ||
                translate('cmp-destination-validation-message', 'Destination is required')
            }
          }}
        />
      ) : null}

      <Controller
        render={({ onChange, onBlur, value, name }) => (
          <DateRangePicker
            ref={DateRangePickerRef}
            activeTab={props.activeTab}
            currentTab={props.currentTab}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            labels={{
              departure: translate(props.labels.departure || 'Departure'),
              return: translate(props.labels.return || 'Return')
            }}
            startDateErrorMessage={<ErrorMessage errors={errors} name="dates" />}
            endDateErrorMessage={
              props.selectedReturnDate === undefined &&
              props.selectedDepartureDate !== undefined && (
                <ErrorMessage errors={errors} name="dates" />
              )
            }
            startDateHasError={!!errors['dates'] && props.selectedDepartureDate === undefined}
            endDateHasError={
              props.singleDateSelection
                ? !!errors['dates'] &&
                  props.selectedReturnDate === undefined &&
                  props.selectedDepartureDate !== undefined
                : !!errors['dates'] &&
                  ((props.selectedReturnDate === undefined &&
                    props.selectedDepartureDate !== undefined) ||
                    (props.selectedReturnDate === undefined &&
                      props.selectedDepartureDate === undefined))
            }
            defaultValue={props.defaultDates}
            timeOptions={timeOptions}
            showTimePickers={!!props.showTimePickers}
            singleSelection={props.singleDateSelection}
            minDate={props.blackoutDates?.minDate}
            maxDate={props.blackoutDates?.maxDate}
            maxRange={props.maxTripDuration}
            dateFormat={datePicker?.dateFormat}
            dayFormat={datePicker?.dayFormat ?? 'D'}
            dateFormatOnEdit={datePicker?.dateFormatOnEdit}
            enableAutomaticStatusNavBarButtons={datePicker?.enableAutomaticStatusNavBarButtons}
            showFlexibleDates={flexibleDates?.enabled}
            flexibleDates={flexibleDates?.defaultValue}
            onChange={(e) => {
              props.updateSelectedDepartureDate(e.start);
              props.updateSelectedReturnDate(e.end);
              if (e.start !== undefined && e.start !== props.selectedDepartureDate) {
                props.onDepartureDateChange(e.start, e.end);
              } else if (e.end !== undefined && e.end !== props.selectedReturnDate) {
                props.onReturnDateChange(e.end, e.start);
              }
              if (errors?.dates !== undefined) {
                if (props?.singleDateSelection) {
                  if (e?.start === undefined || e?.start === null) {
                    if (props?.trigger) {
                      props?.trigger('dates');
                    }
                  }
                } else {
                  if (
                    e?.start === undefined ||
                    e?.start === null ||
                    e?.end === undefined ||
                    e?.end === null
                  ) {
                    if (props?.trigger) {
                      props?.trigger('dates');
                    }
                  }
                }
              }
            }}
            onKeyDownHandleEnterAction={(e: any) => {
              if (e.keyCode === ENTER_KEY) {
                setTimeout(() => {
                  if (props?.trigger) {
                    props?.trigger('dates');
                  }
                  DateRangePickerRef?.current?.handleEnterAction();
                }, 200);
              }
            }}
            trigger={props?.trigger}
            fetchBlackoutDates={props?.fetchBlackoutDates}
            blackoutDatesIntegration={props?.blackoutDatesIntegration}
            selectedOrigin={selectedOrigin}
            selectedDestination={selectedDestination}
            flightType={props?.flightType}
            setValue={props?.setValue}
            getValues={props?.getValues}
          />
        )}
        name="dates"
        rules={{
          validate: {
            required: (value: Dates) => {
              if (props.singleDateSelection) {
                return (
                  !!props.selectedDepartureDate ||
                  translate('cmp-date-range-start-validation-message', 'Pick a start date')
                );
              } else {
                if (!!value?.start === false && !!value?.end === true) {
                  return translate('cmp-date-range-start-validation-message', 'Pick a start date');
                } else if (!!value?.start === true && !!value?.end === false) {
                  return translate('cmp-date-range-end-validation-message', 'Pick an end date');
                } else {
                  return (
                    !!props.selectedDepartureDate ||
                    !!props.selectedReturnDate ||
                    translate('cmp-date-range-validation-message', 'Pick a date range')
                  );
                }
              }
            },
            requiredStart: (value) =>
              !!props.selectedDepartureDate ||
              translate('cmp-date-range-start-validation-message', 'Pick a start date'),
            requiredEnd: (value) => {
              return (
                props.singleDateSelection ||
                !!props.selectedReturnDate ||
                translate('cmp-date-range-end-validation-message', 'Pick an end date')
              );
            }
          }
        }}
        control={props.control}
      />
      {props.showAirlines ? (
        <Controller
          render={({ onChange }) => (
            <div>
              <Combobox
                label={translate('cmp-booking-filter-airlines-label', 'Airlines')}
                options={airlinesOptions}
                onChange={(newValue) => {
                  if (newValue.value && newValue.value !== selectedAirlines?.value) {
                    props.onAirlineChange(newValue);
                  }
                  onChange(newValue);
                }}
                defaultValue={props.prepopulatedAirlines ?? ''}
                errorMessage={<ErrorMessage errors={errors} name="airlines" />}
                hasError={
                  !!errors['airlines'] ||
                  (!disabledArLineFilter() &&
                    airlinesOptions.length === 0 &&
                    fetchAirlines.length !== 0)
                }
                placeholder={translate('cmp-booking-flight-airlines-placeholder', 'Choose Airline')}
                ariaLabelIconClose={translate('cmp-clear-icon', 'Clear')}
                ariaLabelIconToggle={translate('cmp-clear-icon-toggle', 'Toggle')}
                disabled={disabledArLineFilter()}
                {...airline}
              />
              {!disabledArLineFilter() &&
                airlinesOptions.length === 0 &&
                fetchAirlines.length !== 0 && (
                  <p style={{ fontSize: '12px' }}>
                    {translate(
                      'cmp-airlines-validation-message-no-airlines',
                      'No airlines available for the selected route'
                    )}
                  </p>
                )}
            </div>
          )}
          name="airlines"
          control={props.control}
          rules={{
            validate: {
              required: (airline) => {
                const newAirline = airline === undefined ? { label: '', value: '' } : airline;
                if (!disabledArLineFilter()) {
                  if (newAirline.label === '') {
                    return (
                      !!newAirline.label ||
                      translate('cmp-airlines-validation-message', 'Airlines is required')
                    );
                  }
                }
              }
            }
          }}
        />
      ) : null}
      <Tiles columns={props.ctaHalfWidth ? 2 : undefined} colGap={2} variant="layout.ctaContainer">
        {props.ctaHalfWidth ? <div /> : null}
        <Button
          type="submit"
          variant="primaryLg"
          onClick={() => {
            if (props?.onSubmit) {
              props?.onSubmit();
            }
          }}
        >
          {translate(labels.submit || 'Find Booking')}
        </Button>
      </Tiles>
      {props.disclaimer?.enabled ? (
        <Disclaimer label={'cmp-booking-popup-disclaimer'} {...props.disclaimer} />
      ) : null}
    </Columns>
  );
}
